// Copyright (c) Docugami, Inc. All rights reserved.
import { useEffect, useState } from 'react';
import { ClusterDocument, DocumentApi } from '../networking/api';
import { DocxCustomProperty, getCustomProperty, setCustomProperty } from '../components/doc_hosting/doc_hosts/word/util/WordMetadataHelpers';
import { useDeepLinkInitializer } from '../hooks/deepLinkInitializer';
import { useSelector } from 'react-redux';
import { AppState } from '../store';
import { useNavigationService, NavigationType } from '../hooks/navigationService';
import { AaUnknownDocumentMessage } from '../components/assisted_authoring/AaUnknownDocumentMessage';
import { StatusBar } from '../components/file_management/status-bar';
import { DgFlexCenteredColumn } from '../components/common/DgCommonStyledComponents';
import { TelemetryPageView } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';

enum InitializationSteps {
    WaitingForDeepLinkInitializer,
    DeepLinkInitialized,
    InitializatingOffice,
}

export const WordLandingPage = () => {
    const [initializationStep, setInitializationStep] = useState<InitializationSteps>(InitializationSteps.WaitingForDeepLinkInitializer);
    const [wordDocNotRecognized, setWordDocNotRecognized] = useState(false);

    const organizations = useSelector((state: AppState) => state.organizations);

    const [deepLinkInitialized] = useDeepLinkInitializer();
    const navService = useNavigationService();

    function moveToStep(step: InitializationSteps): void {
        console.log(`Landing Page: moving to initialization step ${step}`);
        setInitializationStep(step);
    }

    useEffect(() => {
        Telemetry.trackPageView(TelemetryPageView.WordAddin);
    }, []);

    useEffect(() => {
        if (deepLinkInitialized && initializationStep === InitializationSteps.WaitingForDeepLinkInitializer) {
            moveToStep(InitializationSteps.DeepLinkInitialized);
        }
    }, [deepLinkInitialized, initializationStep])

    useEffect(() => {
        switch (initializationStep) {
            case InitializationSteps.DeepLinkInitialized:
                console.assert(organizations.organizationList.fetchState.fetched);
                if (organizations.organizationList.allIds.length <= 0) {
                    setWordDocNotRecognized(true);
                }
                else {
                    if ((window as any)._OfficeMode) {
                        moveToStep(InitializationSteps.InitializatingOffice);
                        loadOfficeTaskpane();
                    } else {
                        setWordDocNotRecognized(true);
                    }

                }

                return;

            default:
                return;
        }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initializationStep])

    function loadOfficeTaskpane(): void {
        Word.run(async (context) => {
            let folderId = await getCustomProperty(context, DocxCustomProperty.FolderId);
            const documentApi = new DocumentApi();

            if (folderId) {
                try {
                    const docs: ClusterDocument[] = (await documentApi.documentGetDocumentsFromFolderIds([folderId])).data;
                    if (docs?.length !== 1) {
                        console.log(`Stamped folderid not found: ${folderId}`);
                        folderId = '';
                    }
                } catch (error) {
                    console.log(`Stamped folderid not found: ${folderId}`);
                    folderId = '';
                }
            }

            if (!folderId) {
                // Get the filename of the currently open doc
                const docUrl: string = Office.context.document.url;
                let parts = docUrl.split('/');

                if (parts.length === 1) {
                    parts = docUrl.split('\\');
                }

                const docName: string = parts[parts.length - 1];
                console.log(`finding FolderId for Word Doc: ${docName}`);

                try {
                    const docInfo = (await documentApi.documentGetDocument(docName)).data;

                    if (!docInfo) {
                        throw new Error();
                    }

                    folderId = docInfo.folderId;
                    console.log(`Saving FolderId to Document: ${folderId}`);
                    await setCustomProperty(context, DocxCustomProperty.FolderId, folderId);
                } catch (error) {
                    console.log(`failed to navigate to Word Doc: ${docName}`);
                    console.log(error);
                    setWordDocNotRecognized(true);
                }
            } else {
                console.log(`Using folderId cached in custom property: ${folderId}`);
            }

            if (folderId) {
                console.log(`Navigating for Word Doc: ${folderId}`);
                navService.navToDocumentPage(
                    NavigationType.ReplaceCurrentPage,
                    folderId.split(":")[0],
                    folderId);
            }
        })
    }

    return wordDocNotRecognized ? <AaUnknownDocumentMessage /> :
        <DgFlexCenteredColumn style={{ width: '100%' }}>
            <div key="progress" style={{ width: "100px", height: "11px" }}>
                <StatusBar isInfinite={true} />
            </div>
        </DgFlexCenteredColumn>

}
